/**
 * Phone-module preview data stays local so `/m/preview` needs no Azure session.
 */
import { getWeekDateStrings, toLocalDateString } from '../../utils/timesheetDates';

const NDE_ROWS = [
  {
    id: 'nde-101',
    date: '2026-09-02',
    welderNumber: 'W-104',
    ndeType: 'X-Ray',
    isoSheetNumber: 'ISO-P-014',
    lineNumber: 'PW-201-6-CS',
    fluidCode: 'PW',
    qtyJoint: 14,
    qtyNde: 2,
    qtyFailed: 0,
    comments: 'First pass clear.',
  },
  {
    id: 'nde-102',
    date: '2026-09-01',
    welderNumber: 'W-121',
    ndeType: 'UT',
    isoSheetNumber: 'ISO-P-022',
    lineNumber: 'IA-118-2-SS',
    fluidCode: 'IA',
    qtyJoint: 8,
    qtyNde: 1,
    qtyFailed: 1,
    comments: 'Repair weld queued for retest.',
  },
];

const TESTING_ROWS = [
  {
    id: 'test-201',
    isoSheetNumber: 'ISO-P-014',
    testPackageNumber: 'TP-12',
    lineSystemId: 'PW-201',
    fluidCode: 'PW',
    systemDescription: 'Process water header',
    designCode: 'B31.3',
    designPressPsi: 150,
    designTempF: 180,
    testPressPsi: 225,
    testType: 'Hydrostatic',
    testMedium: 'Water (Filtered)',
    holdTimeMin: 60,
    status: 'In Progress',
    testDate: '2026-09-04',
    inspector: 'W-104',
    signOff: '',
    notes: 'Awaiting final isolation check.',
  },
  {
    id: 'test-202',
    isoSheetNumber: 'ISO-P-031',
    testPackageNumber: 'TP-18',
    lineSystemId: 'IA-118',
    fluidCode: 'IA',
    systemDescription: 'Instrument air branch',
    designCode: 'B31.3',
    designPressPsi: 120,
    designTempF: 100,
    testPressPsi: 180,
    testType: 'Pneumatic',
    testMedium: 'Dry Nitrogen',
    holdTimeMin: 30,
    status: 'Pending',
    testDate: '',
    inspector: '',
    signOff: '',
    notes: '',
  },
];

const CONTROL_CHART_ROWS = [
  {
    id: 'change-301',
    new_extra_number: 'E-019',
    description: 'Add supports at process water expansion loop',
    status: 'submitted',
    notes: 'Client review expected this week.',
    discipline: 'Piping',
  },
  {
    id: 'change-302',
    new_extra_number: 'E-021',
    description: 'Revise MCC feeder routing in electrical room',
    status: 'tracking',
    notes: 'Waiting on issued-for-construction drawing.',
    discipline: 'Electrical',
  },
];

export const getDemoModuleRows = (projectId, moduleId) => {
  if (String(projectId) !== 'demo-71940') return [];
  const rows = {
    'piping-nde': NDE_ROWS,
    'piping-testing': TESTING_ROWS,
    'control-chart': CONTROL_CHART_ROWS,
  }[moduleId] || [];
  return rows.map((row) => ({ ...row }));
};

export const DEMO_TODAY_ENTRIES = [
  {
    id: 'today-1',
    date: '2026-09-03',
    project_id: 'demo-71940',
    job_number: '71940',
    empl_code: '1042',
    empl_name: 'Alex Martin',
    op_code: '21001',
    reg: 8,
    t12: 0,
    dbl: 0,
  },
];

export const DEMO_EMPLOYEE = {
  emp_code: '1042',
  emp_name: 'Alex Martin',
  trade: 'PIP',
  division: 'Field',
};

export const DEMO_EMPLOYEES = [
  DEMO_EMPLOYEE,
  { emp_code: '1108', emp_name: 'Sam Reid', trade: 'PIP', division: 'Field' },
  { emp_code: '2214', emp_name: 'Jordan Lee', trade: 'PIP', division: 'Field' },
];

const demoWeek = getWeekDateStrings(toLocalDateString(new Date()), 0);

export const demoEntriesForWeek = (weekStart, empCode) => {
  const week = getWeekDateStrings(weekStart, 0);
  const start = week[0]?.dateStr;
  const end = week[6]?.dateStr;
  if (!start || !end) return [];
  return DEMO_WEEK_ENTRIES.filter((row) => {
    if (empCode && String(row.empl_code) !== String(empCode)) return false;
    const date = String(row.date || '').split('T')[0];
    return date >= start && date <= end;
  });
};

export const DEMO_WEEK_ENTRIES = [
  {
    id: 'week-1',
    date: demoWeek[2]?.dateStr || '2026-09-01',
    project_id: 'demo-71940',
    job_number: '71940',
    op_code: '21001',
    op_code_description: 'PIPING — FABRICATION',
    empl_code: '1042',
    empl_name: 'Alex Martin',
    reg: 8,
    t12: 0,
    dbl: 0,
    r_and_b: 125,
    meals: 20,
    travel_2: 0,
  },
  {
    id: 'week-2',
    date: demoWeek[3]?.dateStr || '2026-09-02',
    project_id: 'demo-71940',
    job_number: '71940',
    op_code: '21001',
    op_code_description: 'PIPING — FABRICATION',
    empl_code: '1042',
    empl_name: 'Alex Martin',
    reg: 8,
    t12: 2,
    dbl: 0,
    r_and_b: 0,
    meals: 0,
    travel_2: 45,
  },
  {
    id: 'week-3',
    date: demoWeek[4]?.dateStr || '2026-09-03',
    project_id: 'demo-71940',
    job_number: '71940',
    op_code: '21001',
    op_code_description: 'PIPING — FABRICATION',
    empl_code: '1108',
    empl_name: 'Sam Reid',
    reg: 8,
    t12: 0,
    dbl: 0,
    r_and_b: 150,
    meals: 25,
    travel_2: 60,
  },
];

export const DEMO_CREWS = [
  {
    id: 'crew-1',
    crew_name: 'Piping · 71940',
    head_emp_code: '1042',
    head_emp_name: 'Alex Martin',
    members: [
      { emp_code: '1108', emp_name: 'Sam Reid', trade: 'PIP' },
      { emp_code: '2214', emp_name: 'Jordan Lee', trade: 'PIP' },
    ],
    projects: [{ project_id: 'demo-71940', job_number: '71940', discipline_code: 'PIP' }],
  },
];

